// Clean URL Router for Fun Old Times
import logger from './logger.js';
import { focusGroupFeedHash, isGroupFeedPath } from './group-feed-hash.js';
import { parseRoutePath } from './route-path.js';
import { getFullCurrentPath } from './return-to-url.js';
import { isLandingRoute, isProtectedRoute } from './auth-routes.js';
import { isGroupInviteRoute } from './group-invite-route.js';
import { isEmailVerificationRoute } from './email-verification-route.js';
class Router {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;
    this.isAuthenticated = false; // Track authentication status
    this.init();
  }

  init() {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', (_e) => {
      this.handleRoute(window.location.pathname, false);
    });

    window.addEventListener('hashchange', () => {
      if (isGroupFeedPath()) {
        const feed = document.querySelector('group-shell posts-feed');
        focusGroupFeedHash({ feed }).catch((err) => {
          logger.warn('Failed to focus group post from hash change', err);
        });
      }
    });

    // Intercept navigation links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (link && this.isInternalLink(link, e)) {
        e.preventDefault();
        this.navigate(link.getAttribute('href'));
      }
    });

    // Listen for auth status loaded to re-trigger section shown events
    document.addEventListener('auth-status-loaded', (e) => {
      const { authenticated } = e.detail;
      this.isAuthenticated = authenticated;

      // Update logo link after auth status is known
      this.updateLogoLink(window.location.pathname);

      if (authenticated) {
        // Re-call showSection for the current route to ensure components initialize properly
        const currentPath = window.location.pathname;
        this.handleRoute(currentPath, false);
      }
    });

    // Listen for auth-success events (login/register) to update authentication state
    document.addEventListener('auth-success', (e) => {
      const { user } = e.detail;
      if (user) {
        this.isAuthenticated = true;
        this.updateLogoLink(window.location.pathname);
      }
    });

    // Listen for logout events to update authentication state
    document.addEventListener('user-logged-out', () => {
      this.isAuthenticated = false;
      // Update logo link after logout
      this.updateLogoLink(window.location.pathname);
    });
  }

  doInitialPageLoad() {
    // Handle initial page load
    this.handleRoute(window.location.pathname, false);
  }

  // Register a route
  addRoute(path, handler) {
    this.routes.set(path, handler);
  }

  splitRoutePath(path) {
    if (path.startsWith('#')) {
      return parseRoutePath(path, window.location.pathname);
    }

    return parseRoutePath(path);
  }

  // Navigate to a route
  navigate(path, addToHistory = true, forceReload = false) {
    // Force reload if requested
    if (forceReload) {
      window.location.href = path;
      return;
    }

    const { pathname, search, hash } = this.splitRoutePath(path);
    const fullPath = `${pathname}${search}${hash}`;
    const currentFull = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (addToHistory && fullPath !== currentFull) {
      window.history.pushState({}, '', fullPath);
    }

    if (hash && pathname === window.location.pathname) {
      if (isGroupFeedPath(pathname)) {
        const feed = document.querySelector('group-shell posts-feed');
        focusGroupFeedHash({ feed }).catch((err) => {
          logger.warn('Failed to focus group post from hash navigation', err);
        });
        return;
      }

      const releaseNotes = document.querySelector('release-notes');
      if (releaseNotes && typeof releaseNotes.scrollToVersionHash === 'function') {
        releaseNotes.scrollToVersionHash();
        return;
      }
    }

    this.handleRoute(pathname, addToHistory);
  }

  // Handle route change
  async handleRoute(path, _addToHistory = true) {
    logger.info(`🧭 Navigating to: ${path}`);

    // Skip RSS feeds and other non-HTML routes - let the server handle them
    if (path.endsWith('.rss') || path.endsWith('.xml') || path.endsWith('.json')) {
      // Force a full page reload for these routes so the server can handle them
      window.location.href = path;
      return;
    }

    // Legacy /help URLs → /docs
    if (path === '/help' || path.startsWith('/help/')) {
      const docsPath = path.replace(/^\/help/, '/docs');
      this.navigate(docsPath, _addToHistory);
      return;
    }

    // Help screenshots — let the server serve static files, not the docs SPA
    if (path.startsWith('/docs/assets/')) {
      window.location.href = path;
      return;
    }

    this.currentRoute = path;

    // Update active navigation
    this.updateActiveNavigation(path);

    const isGuestRoute = path.startsWith('/events/guest');
    if (!isGuestRoute) {
      this.resetGuestRouteLayout();
    }

    // Handle special routes that don't use MainContent
    if (path === '/reset-password') {
      await this.handleResetPassword();
    } else if (path.startsWith('/invite/')) {
      await this.handleInvitation();
    } else if (path.startsWith('/verify-email/')) {
      await this.handleEmailVerification();
    } else if (path.startsWith('/events/guest')) {
      await this.handleGuestRSVP();
    } else if (path === '/privacy' || path === '/terms' || path === '/release-notes' || path === '/docs' || path.startsWith('/docs/')) {
      const pageMap = {
        '/privacy': 'privacy',
        '/terms': 'terms',
        '/release-notes': 'release-notes'
      };
      const page = pageMap[path] || (path.startsWith('/docs') ? 'docs' : null);
      if (page) {
        document.dispatchEvent(new CustomEvent('public-page-request', {
          detail: { page }
        }));
      }
      // Update logo link and page title before returning
      this.updatePageTitle(path);
      this.updateLogoLink(path);
      return;
    } else if (path === '/login') {
      if (this.checkAuthStatus()) {
        this.navigate('/', true);
        return;
      }
      await this.showLogin();
    } else if (path === '/register') {
      await this.showRegister();
    } else {
      // Check if this is a landing route for unauthenticated users
      const isAuthenticated = this.checkAuthStatus();
      if (!isAuthenticated && isLandingRoute(path)) {
        // For unauthenticated users on landing route, show landing page
        // Hide public page first, then show landing page
        document.dispatchEvent(new CustomEvent('hide-public-page'));
        document.dispatchEvent(new CustomEvent('show-landing-page'));
        this.updatePageTitle(path);
        this.updateLogoLink(path);
        return;
      }

      if (!isAuthenticated && isProtectedRoute(path)) {
        document.dispatchEvent(new CustomEvent('hide-public-page'));

        if (isEmailVerificationRoute(path)) {
          this.hideAppShellForAuth();
          this.updatePageTitle(path);
          this.updateLogoLink(path);
          return;
        }

        if (!isGroupInviteRoute(path)) {
          await this.redirectToLogin();
          return;
        }
      }

      // For all other routes, hide public page and show main content
      // Dispatch event to hide public page (handled in main.js)
      document.dispatchEvent(new CustomEvent('hide-public-page'));

      // Trigger the MainContent component to handle the route
      const mainContent = document.querySelector('main-content');
      if (mainContent) {
        // Ensure main content is shown
        if (typeof window.showMainContent === 'function') {
          window.showMainContent();
        }

        // Dispatch route change event to MainContent
        const event = new CustomEvent('route-changed', {
          detail: { route: path }
        });
        document.dispatchEvent(event);
      } else {
        logger.warn('MainContent component not found, falling back to old routing');
        // Fallback to old routing for compatibility
        await this.handleRouteLegacy(path);
      }
    }

    // Update page title
    this.updatePageTitle(path);

    // Update logo link based on current route
    this.updateLogoLink(path);
  }

  updateLogoLink(path) {
    const logoLink = document.querySelector('.site-title-link');
    if (!logoLink) return;

    // Check if we're on a landing route
    const isLandingRoute = path === '/' || path === '/home';
    // Use dynamic auth check for accurate state
    const isAuthenticated = this.checkAuthStatus();

    if (path === '/release-notes') {
      // On release notes: go to home/landing depending on auth status
      logoLink.setAttribute('href', '/');
      logoLink.setAttribute('title', isAuthenticated ? 'Go to Home' : 'Go to Landing Page');
    } else if (!isAuthenticated && isLandingRoute) {
      // On landing page (unauthenticated): go to release notes
      logoLink.setAttribute('href', '/release-notes');
      logoLink.setAttribute('title', 'View Release Notes');
    } else {
      // Authenticated on home or other routes: go to release notes
      logoLink.setAttribute('href', '/release-notes');
      logoLink.setAttribute('title', 'View Release Notes');
    }
  }

  // Legacy routing for fallback compatibility
  async handleRouteLegacy(path) {
    // Hide all sections first
    this.hideAllSections();

    // Handle specific routes
    if (path === '/' || path === '/home') {
      await this.showSection('home');
    } else if (path === '/friends') {
      await this.showSection('friends');
    } else if (path === '/profile') {
      await this.showSection('profile');
    } else if (path === '/profile/me') {
      await this.showUserProfile('me');
    } else if (path.startsWith('/profile/')) {
      const username = path.split('/')[2];
      await this.showUserProfile(username);
    } else if (path === '/admin' || path.startsWith('/admin/')) {
      await this.showSection('admin');
    } else if (path === '/settings') {
      await this.showSection('settings');
    } else if (path === '/settings/security') {
      await this.showSection('two-factor-settings');
    } else if (path === '/settings/notifications') {
      await this.showSection('notification-settings');
    } else if (path === '/settings/sparkle-sources') {
      await this.showSection('media-sources-settings');
    } else if (path === '/settings/rss-feeds') {
      await this.showSection('rss-feeds');
    } else if (path.match(/^\/post\/\d+\/comment\/\d+$/)) {
      // Handle comment permalinks: /post/123/comment/456
      const pathParts = path.split('/');
      const postId = pathParts[2];
      const commentId = pathParts[4];
      await this.showComment(postId, commentId);
    } else if (path.startsWith('/post/')) {
      const postId = path.split('/')[2];

      // Check if there's a hash fragment for a comment
      const hash = window.location.hash;
      if (hash && hash.startsWith('#comment-')) {
        const commentId = hash.replace('#comment-', '');
        await this.showComment(postId, commentId);
      } else {
        await this.showPost(postId);
      }
    } else {
      // 404 - redirect to home
      this.navigate('/', true);
      return;
    }
  }

  // Show a main section
  async showSection(sectionName) {
    const section = document.getElementById(`${sectionName}-section`);
    if (section) {
      section.classList.remove('hidden');

      // Reset post detail view if switching away from home
      if (sectionName !== 'home') {
        this.hidePostDetail();
      } else {
        // Reset home view when showing home section
        this.resetHomeView();
      }

      // Trigger any section-specific initialization
      const event = new CustomEvent('section-shown', {
        detail: { section: sectionName }
      });
      document.dispatchEvent(event);
    }
  }

  // Hide all content sections
  hideAllSections() {
    document.querySelectorAll('.content-section').forEach(section => {
      section.classList.add('hidden');
    });
  }

  // Update active navigation
  updateActiveNavigation(path) {
    try {
      // Remove active class from all nav links (both nav-link and menu-item classes)
      const navLinks = document.querySelectorAll('.nav-link, .menu-item');
      navLinks.forEach(link => {
        if (link && link.classList) {
          link.classList.remove('active');
        }
      });

      // Add active class to current route
      let activeSelector = null;
      if (path === '/' || path === '/home') {
        activeSelector = 'a[href="/"], a[href="/home"], a[data-route="/"]';
      } else if (path === '/friends') {
        activeSelector = 'a[href="/friends"], a[data-route="/friends"]';
      } else if (path === '/profile' || path.startsWith('/profile/')) {
        activeSelector = 'a[href="/profile"], a[href="/profile/me"], a[data-route="/profile/me"]';
      } else if (path === '/admin' || path.startsWith('/admin/')) {
        activeSelector = 'a[href="/admin"], a[href*="/admin"], a[data-route*="/admin"]';
      } else if (path === '/settings' || path.startsWith('/settings')) {
        activeSelector = 'a[href="/settings"], a[href*="/settings"], a[data-route="/settings"], a[data-route*="/settings"]';
      } else if (path === '/memories') {
        activeSelector = 'a[href="/memories"], a[data-route="/memories"]';
      } else if (path === '/groups' || path.startsWith('/groups/') || path.startsWith('/group/')) {
        activeSelector = 'a[href="/groups"], a[data-route="/groups"]';
      } else if (path === '/mascot-parade') {
        activeSelector = 'a[href="/mascot-parade"], a[data-route="/mascot-parade"]';
      }

      if (activeSelector) {
        const activeLink = document.querySelector(activeSelector);
        if (activeLink && activeLink.classList) {
          activeLink.classList.add('active');
        }
      }
    } catch (error) {
      logger.warn('Error updating active navigation:', error);
    }
  }

  // Update page title
  updatePageTitle(path) {
    let title = 'Fun Old Times';

    if (path === '/friends') {
      title = 'Friends - Fun Old Times';
    } else if (path === '/profile') {
      title = 'Profile - Fun Old Times';
    } else if (path === '/profile/me') {
      title = 'My Profile - Fun Old Times';
    } else if (path.startsWith('/profile/')) {
      const username = path.split('/')[2];
      title = `${username} - Fun Old Times`;
    } else if (path === '/admin') {
      title = 'Admin - Fun Old Times';
    } else if (path === '/admin/audit-logs') {
      title = 'Audit Logs - Admin - Fun Old Times';
    } else if (path === '/admin/users') {
      title = 'User Management - Admin - Fun Old Times';
    } else if (path === '/admin/prompts') {
      title = 'Writing Prompts - Admin - Fun Old Times';
    } else if (path === '/admin/stats') {
      title = 'Site Statistics - Admin - Fun Old Times';
    } else if (path === '/admin/image-flags') {
      title = 'Image Flags - Admin - Fun Old Times';
    } else if (path === '/admin/widget-flags') {
      title = 'Widget Flags - Admin - Fun Old Times';
    } else if (path === '/admin/blocked-domains') {
      title = 'Blocked Domains - Admin - Fun Old Times';
    } else if (path === '/admin/oauth-clients') {
      title = 'OAuth Clients - Admin - Fun Old Times';
    } else if (path === '/admin/feature-flags') {
      title = 'Feature Flags - Admin - Fun Old Times';
    } else if (path.startsWith('/admin/')) {
      title = 'Admin - Fun Old Times';
    } else if (path === '/settings') {
      title = 'Settings - Fun Old Times';
    } else if (path === '/settings/security') {
      title = 'Security Settings - Fun Old Times';
    } else if (path === '/settings/notifications') {
      title = 'Notification Settings - Fun Old Times';
    } else if (path === '/settings/sparkle-sources') {
      title = 'Sparkle Sources - Fun Old Times';
    } else if (path === '/settings/rss-feeds') {
      title = 'RSS Feeds Settings - Fun Old Times';
    } else if (path === '/memories') {
      title = 'Memories - Fun Old Times';
    } else if (path === '/mascot-parade') {
      title = 'Mascot Parade - Fun Old Times';
    } else if (path.match(/^\/group\/(\d+)\/post\/(\d+)\/comment\/(\d+)$/)) {
      title = 'Group comment - Fun Old Times';
    } else if (path.match(/^\/group\/(\d+)\/post\/(\d+)$/)) {
      title = 'Group post - Fun Old Times';
    } else if (path.match(/^\/groups\/invites\//)) {
      title = 'Group Invitation - Fun Old Times';
    } else if (path === '/groups') {
      title = 'Groups - Fun Old Times';
    } else if (path === '/groups/new') {
      title = 'Create Group - Fun Old Times';
    } else if (path.match(/^\/groups\/(\d+)\/settings$/)) {
      title = 'Group Settings - Fun Old Times';
    } else if (path.match(/^\/groups\/(\d+)\/threads\/(\d+)$/)) {
      title = 'Group thread - Fun Old Times';
    } else if (path.match(/^\/groups\/(\d+)$/)) {
      title = 'Group - Fun Old Times';
    } else if (path.startsWith('/groups/')) {
      title = 'Groups - Fun Old Times';
    } else if (path === '/reset-password') {
      title = 'Reset Password - Fun Old Times';
    } else if (path.startsWith('/post/')) {
      title = 'Post - Fun Old Times';
    }
    // For unknown routes, default title "Fun Old Times" is used
    // The 404 component will display the "Page Not Found" message

    document.title = title;
  }

  async redirectToLogin() {
    this.setAuthReturnTo(getFullCurrentPath());
    this.hideAppShellForAuth();
    window.history.replaceState({}, '', '/login');
    this.currentRoute = '/login';
    await this.showLogin();
    this.updatePageTitle('/login');
    this.updateLogoLink('/login');
  }

  hideAppShellForAuth() {
    const loadingScreen = document.querySelector('#loadingScreen');
    const mainContent = document.querySelector('.main-content');
    const guestHost = document.getElementById('guest-route-host');

    if (loadingScreen) {
      loadingScreen.style.display = 'none';
    }
    if (mainContent) {
      mainContent.style.display = 'none';
    }
    if (guestHost) {
      guestHost.classList.add('hidden');
      guestHost.innerHTML = '';
    }
  }

  // Show login modal
  async showLogin() {
    this.hideAppShellForAuth();
    let authModal = document.querySelector('auth-modal');
    if (!authModal) {
      authModal = document.createElement('auth-modal');
      document.body.appendChild(authModal);
      // Wait for custom element to be defined and connected
      await customElements.whenDefined('auth-modal');
      // Give it a moment for connectedCallback to run
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    // Ensure the element is connected and has the open method
    if (authModal.isConnected && typeof authModal.open === 'function') {
      authModal.open('login', true);
    } else {
      // Fallback: wait a bit more and try again
      await new Promise(resolve => setTimeout(resolve, 100));
      if (typeof authModal.open === 'function') {
        authModal.open('login', true);
      } else {
        window.logger.error('AuthModal open method not available');
      }
    }
  }

  // Show register modal
  async showRegister() {
    let authModal = document.querySelector('auth-modal');
    if (!authModal) {
      authModal = document.createElement('auth-modal');
      document.body.appendChild(authModal);
      // Wait for custom element to be defined and connected
      await customElements.whenDefined('auth-modal');
      // Give it a moment for connectedCallback to run
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    // Ensure the element is connected and has the open method
    if (authModal.isConnected && typeof authModal.open === 'function') {
      authModal.open('register', true);
    } else {
      // Fallback: wait a bit more and try again
      await new Promise(resolve => setTimeout(resolve, 100));
      if (typeof authModal.open === 'function') {
        authModal.open('register', true);
      } else {
        window.logger.error('AuthModal open method not available');
      }
    }
  }

  setAuthReturnTo(path) {
    sessionStorage.setItem('fotAuthReturnTo', path);
  }

  navigateAfterAuth() {
    const returnTo = sessionStorage.getItem('fotAuthReturnTo');
    if (returnTo) {
      sessionStorage.removeItem('fotAuthReturnTo');
      this.navigate(returnTo, true);
      return;
    }
    const path = window.location.pathname;
    if (path === '/login' || path === '/register' || path.startsWith('/invite/')) {
      this.navigate('/', true);
    }
  }

  async openAuthModal(mode = 'login', options = {}) {
    let authModal = document.querySelector('auth-modal');
    if (!authModal) {
      authModal = document.createElement('auth-modal');
      document.body.appendChild(authModal);
      await customElements.whenDefined('auth-modal');
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    if (mode === 'register' && typeof authModal.openRegisterWithOptions === 'function') {
      authModal.openRegisterWithOptions(options);
    } else if (typeof authModal.open === 'function') {
      authModal.open(mode, false);
    }
  }

  // Handle password reset page
  async handleResetPassword() {
    // Get the reset token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      // No token provided, redirect to home
      this.navigate('/', true);
      return;
    }

    // Then open the auth modal in reset-password mode with the token
    setTimeout(() => {
      const authModal = document.querySelector('auth-modal');
      if (authModal) {
        authModal.openResetPassword(token);
      }
    }, 100);
  }

  // Handle invitation page
  async handleInvitation() {
    // Extract invitation token from URL
    const pathParts = window.location.pathname.split('/');
    const invitationToken = pathParts[2]; // /invite/TOKEN

    if (!invitationToken) {
      // No token provided, redirect to home
      this.navigate('/', true);
      return;
    }

    // Show home page first using MainContent
    const mainContent = document.querySelector('main-content');
    if (mainContent) {
      const event = new CustomEvent('route-changed', {
        detail: { route: '/' }
      });
      document.dispatchEvent(event);
    } else {
      await this.showSection('home');
    }

    // Then open the auth modal in register mode with the invitation token
    setTimeout(() => {
      const authModal = document.querySelector('auth-modal');
      if (authModal) {
        authModal.openRegister(invitationToken);
      }
    }, 100);
  }

  // Handle email verification
  async handleEmailVerification() {
    // Extract verification token from URL
    const pathParts = window.location.pathname.split('/');
    const verificationToken = pathParts[2]; // /verify-email/TOKEN

    if (!verificationToken) {
      // No token provided, redirect to home
      this.navigate('/', true);
      return;
    }

    try {
      // Call the verification API
      const response = await window.API.request(`/auth/verify-email/${verificationToken}`, {
        method: 'GET'
      });

      if (response.verified) {
        // Show success message and redirect to login
        const mainContent = document.querySelector('main-content');
        if (mainContent) {
          const event = new CustomEvent('route-changed', {
            detail: { route: '/' }
          });
          document.dispatchEvent(event);
        } else {
          this.showSection('home');
        }
        setTimeout(() => {
          const authModal = document.querySelector('auth-modal');
          if (authModal) {
            authModal.open('login');
            authModal.showAlert('Email verified successfully! You can now log in.', 'success');
          }
        }, 100);
      }
    } catch (error) {
      logger.error('Email verification failed:', error);
      // Show error and redirect to login
      const mainContent = document.querySelector('main-content');
      if (mainContent) {
        const event = new CustomEvent('route-changed', {
          detail: { route: '/' }
        });
        document.dispatchEvent(event);
      } else {
        this.showSection('home');
      }
      setTimeout(() => {
        const authModal = document.querySelector('auth-modal');
        if (authModal) {
          authModal.open('login');
          authModal.showAlert(error.message || 'Email verification failed', 'error');
        }
      }, 100);
    }
  }

  // Handle guest RSVP
  async handleGuestRSVP() {
    const url = new URL(window.location.href);
    const urlParams = url.searchParams;
    let token = urlParams.get('token');
    let magicToken = urlParams.get('magicToken');

    const pathname = window.location.pathname;

    // Support legacy path formats like /events/guest-verify/:token or /events/guest-access/:magicToken
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length >= 3 && pathSegments[0] === 'events') {
      const routeType = pathSegments[1];
      const pathToken = pathSegments[2];

      if (routeType === 'guest-verify' && pathToken) {
        token = token || pathToken;
        url.searchParams.set('token', token);
        url.searchParams.delete('magicToken');
        magicToken = null;

        // Normalize URL to query-param version to avoid leaking token in path
        url.pathname = '/events/guest-verify';
        window.history.replaceState({}, '', url);
      } else if (routeType === 'guest-access' && pathToken) {
        magicToken = magicToken || pathToken;
        url.searchParams.set('magicToken', pathToken);
        url.pathname = '/events/guest-access';
        window.history.replaceState({}, '', url);
      }
    }

    if (!token && magicToken && !pathname.startsWith('/events/guest-access')) {
      token = magicToken;
      url.searchParams.set('token', token);
      url.searchParams.delete('magicToken');
      magicToken = null;
      window.history.replaceState({}, '', url);
    }

    this.showGuestRouteLayout();

    const guestHost = document.getElementById('guest-route-host');
    if (!guestHost) {
      logger.warn('Guest route host not found');
      return;
    }

    let guestRSVP = guestHost.querySelector('guest-rsvp');
    if (!guestRSVP) {
      guestRSVP = document.createElement('guest-rsvp');
    }

    guestHost.innerHTML = '';
    guestHost.appendChild(guestRSVP);

    if (token) {
      guestRSVP.setAttribute('token', token);
    }
    if (magicToken) {
      url.searchParams.set('magicToken', magicToken);
      window.history.replaceState({}, '', url);
    }
  }

  showGuestRouteLayout() {
    const mainContainer = document.querySelector('.main-content');
    if (mainContainer) {
      mainContainer.style.display = 'block';
    }

    const primaryContent = document.querySelector('main-content');
    if (primaryContent) {
      primaryContent.style.display = 'none';
    }

    const guestHost = document.getElementById('guest-route-host');
    if (guestHost) {
      guestHost.classList.remove('hidden');
    }
  }

  resetGuestRouteLayout() {
    const guestHost = document.getElementById('guest-route-host');
    if (guestHost) {
      guestHost.innerHTML = '';
      guestHost.classList.add('hidden');
    }

    const primaryContent = document.querySelector('main-content');
    if (primaryContent) {
      primaryContent.style.display = '';
    }
  }

  // Show specific user profile
  async showUserProfile(username) {
    // Show the profile section with the user-profile component
    await this.showSection('profile');

    // Trigger event to load the specific user profile
    const event = new CustomEvent('profile-load', {
      detail: { username }
    });
    document.dispatchEvent(event);
  }

  // Show specific post
  async showPost(postId) {
    // Show the home section first
    await this.showSection('home');

    // Then show the post detail component
    setTimeout(() => {
      const postDetail = document.querySelector('post-detail');
      if (postDetail) {
        postDetail.setAttribute('post-id', postId);
        postDetail.style.display = 'block';
      } else {
        // Create and show post detail component
        const newPostDetail = document.createElement('post-detail');
        newPostDetail.setAttribute('post-id', postId);
        newPostDetail.style.display = 'block';

        // Insert after the posts feed
        const postsFeed = document.querySelector('posts-feed');
        if (postsFeed && postsFeed.parentNode) {
          postsFeed.parentNode.insertBefore(newPostDetail, postsFeed.nextSibling);
        }
      }

      // Hide the posts feed to show only the individual post
      const postsFeed = document.querySelector('posts-feed');
      if (postsFeed) {
        postsFeed.style.display = 'none';
      }

      // Hide the post writer
      const postWriter = document.querySelector('post-writer');
      if (postWriter) {
        postWriter.style.display = 'none';
      }
    }, 100);
  }

  // Show specific comment (within a post)
  async showComment(postId, commentId) {
    try {
      // Show the post first
      await this.showPost(postId);

      // Wait for the comments section to be available
      const commentsSection = await this.waitForElement('comments-section');
      if (!commentsSection) {
        logger.warn('Comments section not found');
        return;
      }

      // Trigger comment loading if not already loaded
      if (!commentsSection.commentsLoaded) {
        await commentsSection.loadComments();
      }

      // Wait for the specific comment element to be available
      const commentElement = await this.waitForElement(`[data-comment-id="${commentId}"]`);
      if (!commentElement) {
        logger.warn(`Comment ${commentId} not found in post ${postId}`);
        return;
      }

      // Scroll to the comment
      commentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add a highlight effect
      this.highlightComment(commentElement);
    } catch (error) {
      logger.error('Failed to load comment:', error);
    }
  }

  // Helper to wait for an element to appear in the DOM
  waitForElement(selector, timeout = 5000) {
    return new Promise((resolve) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout fallback
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeout);
    });
  }

  // Helper to highlight a comment
  highlightComment(commentElement) {
    commentElement.classList.add('comment-highlight');

    setTimeout(() => {
      commentElement.classList.remove('comment-highlight');
    }, 3000);
  }

  // Check if link is internal
  isInternalLink(link, event) {
    const href = link.getAttribute('href');
    const target = link.getAttribute('target');

    if (target && target !== '_self') {
      return false;
    }

    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
      return false;
    }

    // Skip external links
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
      return false;
    }

    // Skip hash-only links that we want to handle as routes
    if (href.startsWith('#')) {
      return true;
    }

    // Handle relative and absolute internal paths
    return true;
  }

  // Get current route
  getCurrentRoute() {
    return this.currentRoute;
  }

  // Check if we're on a specific route
  isRoute(path) {
    return this.currentRoute === path;
  }

  // Check authentication status dynamically (fallback to checking auth-status component)
  checkAuthStatus() {
    // First check cached value
    if (this.isAuthenticated) {
      return true;
    }

    // Fallback: check if auth-status component shows user as authenticated
    const authStatus = document.querySelector('auth-status');
    if (authStatus && authStatus.user) {
      this.isAuthenticated = true;
      return true;
    }

    return false;
  }

  // Hide post detail view
  hidePostDetail() {
    const postDetail = document.querySelector('post-detail');
    if (postDetail) {
      postDetail.style.display = 'none';
    }
  }

  // Reset home view to show normal feed
  resetHomeView() {
    const postsFeed = document.querySelector('posts-feed');
    const postWriter = document.querySelector('post-writer');
    const postDetail = document.querySelector('post-detail');

    if (postsFeed) {
      postsFeed.style.display = 'block';
    }

    if (postWriter) {
      postWriter.style.display = 'block';
    }

    if (postDetail) {
      postDetail.style.display = 'none';
    }
  }
}

// Create singleton router
export const router = new Router();

// Make router available globally
window.router = router;

// Export for use in other modules
export default router;

